import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';

const categorySeo: Record<string, { title: string; description: string; keywords: string }> = {
  meishi: {
    title: '美食内容 - 甜蜜美食短视频',
    description: '蜜桃视频美食频道汇聚各地风味美食短视频内容，从街头小吃到精致料理，用镜头记录舌尖上的甜蜜滋味。探索美食制作教程、探店vlog、甜品分享等精彩内容。',
    keywords: '蜜桃视频美食内容,美食短视频,蜜桃视频美食频道,美食制作教程,蜜桃视频甜品,蜜桃视频探店',
  },
  lvxing: {
    title: '旅行内容 - 沉浸式旅行Vlog',
    description: '蜜桃视频旅行频道带你跟随创作者足迹走遍世界，沉浸式旅行Vlog记录不同城市的独特魅力。发现旅行攻略、风景大片、文化体验等甜蜜旅行内容。',
    keywords: '蜜桃视频旅行vlog,旅行短视频,蜜桃视频旅行频道,旅行攻略,蜜桃视频风景,蜜桃视频城市探索',
  },
  shishang: {
    title: '时尚内容 - 穿搭美妆生活美学',
    description: '蜜桃视频时尚频道紧跟潮流趋势，提供穿搭灵感、美妆教程、生活美学等甜蜜内容。打造属于你的甜美风格，发现更多时尚创意短视频。',
    keywords: '蜜桃视频时尚内容,时尚短视频,蜜桃视频穿搭,蜜桃视频美妆,生活美学,潮流搭配',
  },
  mengchong: {
    title: '萌宠内容 - 治愈系宠物短视频',
    description: '蜜桃视频萌宠频道汇集可爱毛孩子的日常，治愈系宠物短视频温暖你的每一天。猫咪、狗狗、小宠物的甜蜜互动，尽在蜜桃视频萌宠频道。',
    keywords: '蜜桃视频萌宠内容,萌宠短视频,蜜桃视频宠物,猫咪视频,狗狗日常,蜜桃视频治愈萌宠',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.id === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);
  const seo = slug ? categorySeo[slug] : null;

  if (!category) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 蜜桃视频`,
    description: category.description,
    url: `https://mitao-video.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '蜜桃视频',
      url: 'https://mitao-video.com',
    },
  };

  return (
    <>
      <SEOHead
        title={seo?.title || `${category.name}内容`}
        description={seo?.description || category.description}
        keywords={seo?.keywords || `蜜桃视频${category.name}内容,${category.name}短视频`}
        canonicalUrl={`https://mitao-video.com/category/${slug}`}
        ogImage={category.image}
        schemaData={[webPageSchema]}
      />

      <div className="pt-20">
        {/* Category Hero */}
        <div className="relative h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={category.image}
            alt={`蜜桃视频${category.name}频道`}
            title={`蜜桃视频${category.name}频道 - 甜蜜生活创意短视频`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/30"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <div className="w-14 h-14 rounded-2xl bg-background-50/20 backdrop-blur-sm flex items-center justify-center mb-4">
                  <i className={`${category.icon} text-background-50 text-2xl`}></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold font-heading text-background-50 mb-3">
                  {category.name}
                </h1>
                <p className="text-background-50/80 text-base leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb + Category Tabs */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-4">
            <div className="flex items-center gap-2 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.id === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Article List */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden cursor-pointer hover:border-primary-200 transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`蜜桃视频 - ${article.title}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-foreground-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i> {article.likes.toLocaleString()} 点赞
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}